import React, { useState } from 'react';
import '../../../shared/style.css';
import { FormApi } from 'final-form';
import ExternalLink from '../../../../../../shared/components/ExternalLink';
import { t, tBB } from '../../../../../../shared/components/l10n/l10n';
import { OnMount } from '../../../../../../shared/components/OnMount';
import { SalesDialogForm, SalesDialogFormProps } from '../../../shared/components/SalesDialogForm';
import { SendAsEInvoice } from '../../../shared/components/SendBy';
import { FullAttachmentOptions, SendEmailOptionsDrawer } from '../../../shared/components/SendEmailOptionsDrawer';
import { bookAndSendTracker } from '../../utils';
import { emailOptionsValidationSchema } from '../../utils/emailOptionsValidationSchema';
import { emailTemplateUpdater } from '../../utils/emailTemplateUpdater';
import MoneyflowEInvoiceDrawer from '../moneyflow/MoneyflowEInvoiceDrawer';
import MoneyflowOfferDetails from '../moneyflow/MoneyflowOfferDetails';
import { BookAndSendFormData, SendByType } from '../types';
import { BookDraftEInvoiceDialogContent } from './BookDraftEInvoiceDialogContent';
import { BookDraftEmailInvoiceDialogContent } from './BookDraftEmailInvoiceDialogContent';
import { useLocation } from 'react-router-dom';

export interface BookDraftInvoiceDialogProps {
    loading: boolean;
    information?: BookAndSendFormData;
    onBook: (value: BookAndSendFormData) => Promise<void>;
    onHide: () => void;
    onToggleMoneyflow: (isToggled: boolean, formValues: BookAndSendFormData) => void;
}

export function BookDraftInvoiceDialog({ loading, information, onBook, onHide, onToggleMoneyflow }: BookDraftInvoiceDialogProps) {
    const location = useLocation();
    const [selectedSendByType, setSelectedSendByType] = useState<SendByType | null>(null);

    const isMoneyflowEnabled = information?.moneyflowOffer?.toggled;
    const sendByType = selectedSendByType ?? information?.sendByType;

    function updater() {
        return [emailTemplateUpdater()];
    }

    function onFormAction(form: FormApi<any>, sendByType: SendByType) {
        form.change('sendByType', Number(sendByType));
        form.blur('sendByType');
        form.submit();
    }

    const open = location.hash === '#book-draft';
    const salesDialogFormProps: Omit<SalesDialogFormProps, 'children'> = {
        loading: loading,
        open,
        onHide,
        ariaLabel: t(`FORM_BOOKANDSEND_INVOICE_HEADER`, {
            DOCUMENT_TYPE: information?.isCreditNote
                ? t`FORM_BOOKANDSEND_DOCUMENT_TYPE_CREDIT_NOTE`
                : t`FORM_BOOKANDSEND_DOCUMENT_TYPE_DRAFT_INVOICE`,
        }),
        className: 'book-and-send-dialog h-[500px]',
        initialData: information,
        onSubmit: onBook,
    };

    return (
        <SalesDialogForm
            {...salesDialogFormProps}
            drawer={GetDrawer(sendByType, information, isMoneyflowEnabled)}
            extra={GetExtra(information, isMoneyflowEnabled)}
            validationSchema={sendByType !== SendByType.oioxml ? emailOptionsValidationSchema() : undefined}
            updates={updater}>
            {(form, drawer) => (
                <>
                    {information && (
                        <>
                            {sendByType === SendByType.oioxml && (
                                <>
                                    <OnMount run={() => !drawer.open && drawer.toggle()} />
                                    <BookDraftEInvoiceDialogContent
                                        information={information}
                                        note={GetNote(isMoneyflowEnabled)}
                                        onConvertToEmail={() => setSelectedSendByType(SendByType.email)}
                                        onCancel={onHide}
                                        formAction={sendByType => onFormAction(form, sendByType)}
                                        onToggleMoneyflow={() =>
                                            onToggleMoneyflow(!isMoneyflowEnabled, form.getState().values as BookAndSendFormData)
                                        }
                                    />
                                </>
                            )}
                            {sendByType !== SendByType.oioxml && (
                                <BookDraftEmailInvoiceDialogContent
                                    onCancel={onHide}
                                    onToggleMoneyflow={() =>
                                        onToggleMoneyflow(!isMoneyflowEnabled, form.getState().values as BookAndSendFormData)
                                    }
                                    onConvertToEInvoice={() => setSelectedSendByType(SendByType.oioxml)}
                                    formAction={sendByType => onFormAction(form, sendByType)}
                                    information={information}
                                    drawer={drawer}
                                />
                            )}
                        </>
                    )}
                </>
            )}
        </SalesDialogForm>
    );
}

function GetDrawer(sendByType: SendByType | undefined, info: any, isMonyFlowEnabled: boolean | undefined) {
    if (sendByType === SendByType.oioxml) {
        if (info && isMonyFlowEnabled) {
            return formValues => <MoneyflowEInvoiceDrawer emails={formValues.moneyflowEinvoiceEmails} />;
        }
        return undefined;
    }
    return formValues => (
        <SendEmailOptionsDrawer hideRemember={formValues.hasEmailTemplates}>
            <FullAttachmentOptions />
        </SendEmailOptionsDrawer>
    );
}

function GetExtra(info: any, isMonyFlowEnabled: boolean | undefined) {
    if (info && isMonyFlowEnabled) {
        return <MoneyflowOfferDetails showAgreementLink={true} data={info.moneyflowOffer} />;
    }
    return undefined;
}

function GetNote(isMonyFlowEnabled: boolean | undefined) {
    if (isMonyFlowEnabled) {
        return <SendEInvoiceInstantPaymentNote />;
    }
    return <SendEInvoiceNote />;
}

function SendEInvoiceInstantPaymentNote() {
    return (
        <SendAsEInvoice
            title={t`FORM_BOOKANDSEND_EINVOICE_INSTANTPAYMENT_TITLE`}
            text={<em>{tBB(`FORM_BOOKANDSEND_EINVOICE_INSTANTPAYMENT_TEXT`)}</em>}
        />
    );
}

function SendEInvoiceNote() {
    return (
        <SendAsEInvoice
            title={t`FORM_BOOKANDSEND_ELECTRONIC_SINGLE_INVOICE_TEXT`}
            text={
                <em>
                    {tBB(`FORM_BOOKANDSEND_ELECTRONIC_SINGLE_INVOICE_INFO`, {
                        __LINK: content => (
                            <ExternalLink
                                href={t`FORM_BOOKANDSEND_ELECTRONIC_SINGLE_INVOICE_INFO_URL`}
                                onClick={() => {
                                    bookAndSendTracker('learnmorelink.open');
                                }}>
                                {content}
                            </ExternalLink>
                        ),
                    })}
                </em>
            }
        />
    );
}
